import React from 'react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    // Log error to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="error-boundary">
          <div className="error-content">
            <div className="error-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
            <h2 className="error-title">Something went wrong</h2>
            <p className="error-message">
              We encountered an unexpected error. Please try again.
            </p>
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="error-details">
                <summary>Error Details</summary>
                <pre>{this.state.error.toString()}</pre>
                {this.state.errorInfo && (
                  <pre>{this.state.errorInfo.componentStack}</pre>
                )}
              </details>
            )}
            <div className="error-actions">
              <button className="btn btn-primary" onClick={this.handleRetry}>
                Try Again
              </button>
              <button className="btn btn-outline" onClick={() => window.location.reload()}>
                Reload Page
              </button>
            </div>
          </div>

          <style jsx>{`
            .error-boundary {
              display: flex;
              align-items: center;
              justify-content: center;
              min-height: 100vh;
              padding: var(--space-xl);
              background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%);
            }

            .error-content {
              text-align: center;
              max-width: 500px;
              padding: var(--space-2xl);
              background: rgba(255, 255, 255, 0.05);
              border: 1px solid rgba(239, 68, 68, 0.3);
              border-radius: var(--radius-xl);
            }

            .error-icon {
              color: var(--color-error);
              margin-bottom: var(--space-lg);
            }

            .error-title {
              font-family: var(--font-display);
              font-size: var(--font-2xl);
              font-weight: 600;
              color: var(--color-neutral-50);
              margin-bottom: var(--space-sm);
            }

            .error-message {
              font-size: var(--font-base);
              color: var(--color-neutral-400);
              margin-bottom: var(--space-lg);
            }

            .error-details {
              text-align: left;
              margin-bottom: var(--space-lg);
              padding: var(--space-md);
              background: rgba(0, 0, 0, 0.3);
              border-radius: var(--radius-md);
              font-size: var(--font-xs);
            }

            .error-details summary {
              cursor: pointer;
              color: var(--color-neutral-300);
              margin-bottom: var(--space-sm);
            }

            .error-details pre {
              color: var(--color-error);
              overflow-x: auto;
              white-space: pre-wrap;
              word-break: break-word;
              margin: 0;
              font-size: var(--font-xs);
            }

            .error-actions {
              display: flex;
              gap: var(--space-md);
              justify-content: center;
            }

            .error-actions .btn {
              min-width: 120px;
            }
          `}</style>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
